import { IPlayer } from "./PlayerController";

interface PlayerRegistration {
    guid: string;
    kind: string;
    player: IPlayer;
}

interface ConsumerRegistration {
    guid: string;
    kind: string;
    onInit?: (player: IPlayer) => void;
    onDispose?: (player: IPlayer) => void;
    onProgress?: (player: IPlayer, progress: number) => void;
}

export class PlayerSync {
    private _players: Map<string, PlayerRegistration> = new Map();
    private _consumers: Map<string, ConsumerRegistration[]> = new Map();

    public registerPlayer(player: PlayerRegistration): DisposableObject | null {
        if (!player.guid) {
            console.error("Player guid is required.");
            return null;
        }

        if (!player.kind) {
            console.error("Player kind is required.");
            return null;
        }

        const key = player.kind + "|" + player.guid;

        if (this._players.has(key)) {
            console.error(`Player with key ${key} already registered.`);
            return null;
        }

        this._players.set(key, player);

        if (this._consumers.has(key)) {
            const consumers = this._consumers.get(key);

            for (const consumer of consumers) {
                if (consumer?.onInit) {
                    consumer.onInit(player.player);
                }

                if (consumer?.onProgress && player.player.isReady) {
                    consumer.onProgress(player.player, player.player.progress);
                }
            }
        }

        player.player.onEnterFrame.add(() => {
            if (!this._consumers.has(key)) {
                return;
            }

            const consumers = this._consumers.get(key);
            for (const consumer of consumers) {
                if (player.player.isReady && consumer?.onProgress) {
                    consumer.onProgress(player.player, player.player.progress);
                }
            }
        });

        return {
            dispose: () => {
                const consumers = this._consumers.get(key);
                if (consumers) {
                    for (const consumer of consumers) {
                        if (consumer?.onDispose) {
                            consumer.onDispose(player.player);
                        }
                    }
                }
                this._players.delete(key);
            }
        };
    }

    public registerConsumer(consumer: ConsumerRegistration): DisposableObject | null {

        if (!consumer.guid) {
            console.error("Consumer guid is required.");
            return null;
        }

        if (!consumer.kind) {
            console.error("Consumer kind is required.");
            return null;
        }

        const key = consumer.kind + "|" + consumer.guid;

        if (!this._consumers.has(key)) {
            this._consumers.set(key, []);
        }

        const consumers = this._consumers.get(key);
        consumers.push(consumer);

        if (this._players.has(key)) {
            const player = this._players.get(key);
            if (consumer?.onInit) {
                consumer.onInit(player.player);
            }

            if (player.player.isReady && consumer?.onProgress) {
                consumer.onProgress(player.player, player.player.progress);
            }
        }

        return {
            dispose: () => {
                const consumers = this._consumers.get(key);
                if (!consumers) {
                    return;
                }
                const index = consumers.indexOf(consumer);
                if (index >= 0) {
                    consumers.splice(index, 1);
                }
                if (consumers.length === 0) {
                    this._consumers.delete(key);
                }
            }
        };
    }
}